import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';
import usePlatform from '@/utils/hooks/usePlatform';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import InlineHelpIcon from '@/components/General/inputs/InlineHelpIcon';
import Calendar from '@/components/Events/Calendar';
import DropdownMenu from '@/components/General/DropdownMenu';
import TimePicker from '@/components/General/inputs/TimePicker';
import SheetModal from '@/components/General/SheetModal';

const formatShortDate = (d) => {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
};


const parseShortDate = (str) => {
    const parts = str.split('/');
    if (parts.length !== 3) return null;
    const [mm, dd, yyyy] = parts.map(Number);
    if (!mm || !dd || !yyyy || mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1900) return null;
    const d = new Date(yyyy, mm - 1, dd);
    if (d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
    return d;
};

const formatShortTime = (d) => {
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const mm = String(minutes).padStart(2, '0');
    return `${displayHours}:${mm} ${ampm}`;
};

const parseShortTime = (str) => {
    const match = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
    if (!match) return null;
    let [, hoursStr, minutesStr, ampm] = match;
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null;
    let finalHours = hours;
    if (ampm && ampm.toUpperCase() === 'PM' && hours !== 12) finalHours += 12;
    if (ampm && ampm.toUpperCase() === 'AM' && hours === 12) finalHours = 0;
    return { hours: finalHours, minutes };
};

const DatePickerInput = ({ value, onUpdate = () => {}, title, required = false, mode = "date", minimumDate = null, maximumDate = null, minuteInterval = 1, helpText, hideClearIcon = false, hideLabel = false, disabled = false, isNotOnSurface = false}) => {
    const [open, setOpen] = useState(false);
    const [tempDate, setTempDate] = useState(() => value ? (value instanceof Date ? new Date(value) : new Date(value)) : new Date());
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState('');
    const [isEditingTime, setIsEditingTime] = useState(false);
    const [editTimeText, setEditTimeText] = useState('');
    const editRef = useRef(null);
    const editTimeRef = useRef(null);
    const pendingMonthRef = useRef(null);
    const pendingYearRef = useRef(null);
    const dayWasPickedRef = useRef(false);
    const justClearedRef = useRef(false);

    const { isNative } = usePlatform();
    const { theme: { colors } } = useUnistyles();

    useEffect(() => {
        if (value) {
            justClearedRef.current = false;
            setTempDate(value instanceof Date ? new Date(value) : new Date(value));
        }
    }, [value]);

    const formatDisplay = useCallback(() => {
        if (!value) return null;
        // Handle date strings - date-only strings (YYYY-MM-DD) are parsed as UTC by default,
        // which causes day-before issues in negative UTC timezones. Add offset to fix.
        // ISO strings with a time component (T...) already parse to the correct local
        // instant - no adjustment needed.
        let d = new Date(value);
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            d = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
        }
        const datePart = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
        if (mode === 'date') return datePart;
        if (mode === 'time') return timePart;
        return `${datePart} at ${timePart}`;
    }, [value, mode]);

    const commitEdit = useCallback((text, timeText = null) => {
        if (!text) return;
        const parsed = parseShortDate(text);
        if (parsed) {
            if (value) {
                const prev = new Date(value);
                parsed.setHours(prev.getHours(), prev.getMinutes(), prev.getSeconds());
            }
            if (mode === 'datetime' && timeText) {
                const parsedTime = parseShortTime(timeText);
                if (parsedTime) {
                    const step = Math.max(1, Number(minuteInterval) || 1);
                    const roundedMinutes = Math.floor(parsedTime.minutes / step) * step;
                    parsed.setHours(parsedTime.hours, roundedMinutes, 0, 0);
                }
            }
            if (minimumDate && parsed < new Date(minimumDate)) return;
            if (maximumDate && parsed > new Date(maximumDate)) return;
            onUpdate(parsed);
        }
    }, [value, onUpdate, minimumDate, maximumDate, mode, minuteInterval]);

    const handleFocus = useCallback(() => {
        // Clear button focuses this input synchronously after calling
        // onUpdate(null) — the value prop is still the old date in that
        // closure, so without this flag the cleared date would be restored.
        if (justClearedRef.current) {
            justClearedRef.current = false;
            setEditText('');
            setIsEditing(true);
            return;
        }
        if (!value) {
            setEditText('');
            setIsEditing(true);
            return;
        }
        // Apply same timezone offset fix for date-only strings to prevent day-before issues
        let d = new Date(value);
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            d = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
        }
        setEditText(formatShortDate(d));
        setIsEditing(true);
    }, [value]);

    const handleBlur = useCallback(() => {
        setIsEditing(false);
        commitEdit(editText, mode === 'datetime' ? editTimeText : null);
    }, [editText, editTimeText, commitEdit, mode]);

    const handleFocusTime = useCallback(() => {
        if (!value) {
            setEditTimeText('');
            setIsEditingTime(true);
            return;
        }
        let d = new Date(value);
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            d = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
        }
        setEditTimeText(formatShortTime(d));
        setIsEditingTime(true);
    }, [value]);

    const handleBlurTime = useCallback(() => {
        setIsEditingTime(false);
        if (editTimeText) {
            const parsedTime = parseShortTime(editTimeText);
            if (parsedTime && value) {
                const step = Math.max(1, Number(minuteInterval) || 1);
                const roundedMinutes = Math.floor(parsedTime.minutes / step) * step;
                const d = new Date(value);
                d.setHours(parsedTime.hours, roundedMinutes, 0, 0);
                onUpdate(d);
            }
        }
    }, [editTimeText, value, onUpdate, minuteInterval]);

    const handleEditKeyDown = useCallback((e) => {
        if (e.nativeEvent.key === 'Enter') {
            setIsEditing(false);
            commitEdit(editText, mode === 'datetime' ? editTimeText : null);
            editRef.current?.blur();
        }
        if (e.nativeEvent.key === 'Escape') {
            setIsEditing(false);
            editRef.current?.blur();
        }
    }, [editText, editTimeText, commitEdit, mode]);

    const handleEditTimeKeyDown = useCallback((e) => {
        if (e.nativeEvent.key === 'Enter') {
            setIsEditingTime(false);
            commitEdit(editText, editTimeText);
            editTimeRef.current?.blur();
        }
        if (e.nativeEvent.key === 'Escape') {
            setIsEditingTime(false);
            editTimeRef.current?.blur();
        }
    }, [editText, editTimeText, commitEdit]);

    const handleDropdownClose = useCallback(() => {
        if (dayWasPickedRef.current) {
            dayWasPickedRef.current = false;
            pendingMonthRef.current = null;
            pendingYearRef.current = null;
            return;
        }
        const pm = pendingMonthRef.current;
        const py = pendingYearRef.current;
        if (pm === null && py === null) return;

        const base = value ? new Date(value) : new Date();
        const newDate = new Date(base);
        if (py !== null) newDate.setFullYear(py);
        if (pm !== null) newDate.setMonth(pm);
        
        pendingMonthRef.current = null;
        pendingYearRef.current = null;

        if (!value || newDate.getTime() !== new Date(value).getTime()) {
            onUpdate(newDate);
        }
    }, [value, onUpdate]);

    const placeholderText = mode === 'time' ? 'Select a Time' : mode === 'datetime' ? 'Select Date & Time' : 'Select a Date';
    const iconName = mode === 'time' ? "time-outline" : "calendar-outline";
    const displayText = formatDisplay();

    const handleNativeConfirm = useCallback(() => {
        onUpdate(tempDate);
        setOpen(false);
    }, [tempDate, onUpdate]);

    const calendarCallbacks = {
        onMonthChange: (m) => { pendingMonthRef.current = m; },
        onYearChange: (y) => { pendingYearRef.current = y; },
    };

    const pickerContent = (close) => {
        if (mode === 'date') {
            return (
                <View style={styles.pickerContainer}>
                    <Calendar
                        calendarWidth={350}
                        minimumDate={minimumDate}
                        maximumDate={maximumDate}
                        initialSelectedDate={value ? (value instanceof Date ? value : new Date(value)) : null}
                        onMonthChange={calendarCallbacks.onMonthChange}
                        onYearChange={calendarCallbacks.onYearChange}
                        onDayPress={(date) => {
                            dayWasPickedRef.current = true;
                            pendingMonthRef.current = null;
                            pendingYearRef.current = null;
                            onUpdate(date);
                            close?.();
                        }}
                    />
                </View>
            );
        }
        if (mode === 'time') {
            return (
                <View style={styles.timeOnlyContainer}>
                    <TimePicker
                        minuteInterval={minuteInterval}
                        forceInterval={minuteInterval > 1}
                        date={tempDate}
                        onChange={(d) => {
                            setTempDate(d);
                            if (!isNative) onUpdate(d);
                        }}
                    />
                </View>
            );
        }
        return (
            <View style={styles.pickerContainer}>
                <Calendar
                    calendarWidth={350}
                    minimumDate={minimumDate}
                    maximumDate={maximumDate}
                    initialSelectedDate={value ? (value instanceof Date ? value : new Date(value)) : null}
                    onMonthChange={calendarCallbacks.onMonthChange}
                    onYearChange={calendarCallbacks.onYearChange}
                    onDayPress={(date) => {
                        dayWasPickedRef.current = true;
                        pendingMonthRef.current = null;
                        pendingYearRef.current = null;
                        const next = new Date(date);
                        next.setHours(tempDate.getHours(), tempDate.getMinutes(), 0, 0);
                        setTempDate(next);
                        if (!isNative) onUpdate(next);
                    }}
                />
                <TimePicker
                    minuteInterval={minuteInterval}
                    forceInterval={minuteInterval > 1}
                    date={tempDate}
                    onChange={(d) => {
                        setTempDate(d);
                        if (!isNative) onUpdate(d);
                    }}
                />
            </View>
        );
    };

    const nativeInputBox = (
        <Pressable testID={`DatePicker-${title}`} style={[styles.input(disabled, isNotOnSurface), disabled && styles.disabled]} onPress={disabled ? undefined : () => setOpen(true)} disabled={disabled}>
            <View style={styles.inputRow}>
                <Unicon name={iconName} size={18} colorVariant="textSecondary"/>
                {displayText
                    ? <Text style={styles.valueText}>{displayText}</Text>
                    : <Text style={styles.placeholderText}>{placeholderText}</Text>
                }
            </View>
        </Pressable>
    );

    return (
        <View style={styles.container}>
            {!hideLabel && (
                <View style={styles.labelContainer}>
                    <Text style={styles.labelText}>
                        {title}
                        {required && <Text style={styles.required}>*</Text>}
                        {!!helpText && <InlineHelpIcon text={helpText} />}
                    </Text>
                </View>
            )}
            {isNative ? (
                <>
                    {nativeInputBox}
                    <SheetModal
                        isOpen={open}
                        title={title}
                        onCancel={() => setOpen(false)}
                        confirmText={mode !== 'date' ? "Confirm" : ""}
                        onConfirm={mode !== 'date' ? handleNativeConfirm : undefined}
                    >
                        <View style={styles.modalContent}>
                            {pickerContent(() => setOpen(false))}
                        </View>
                    </SheetModal>
                </>
            ) : (
                <View style={[styles.input(disabled, isNotOnSurface), disabled && styles.disabled]}>
                    <View style={styles.inputRow}>
                        {disabled ? (
                            <Pressable hitSlop={8}><Unicon name={iconName} size={18} colorVariant="textSecondary"/></Pressable>
                        ) : (
                        <DropdownMenu
                            testID={`DatePicker-${title}`}
                            align="start"
                            triggerComponent={<Pressable hitSlop={8}><Unicon name={iconName} size={18} colorVariant="textSecondary"/></Pressable>}
                            dropdownComponent={(close) => pickerContent(close)}
                            title={title}
                            onClose={handleDropdownClose}
                        />
                        )}
                        {mode !== 'time' ? (
                            <View style={styles.dateTimeInputs}>
                                <TextInput
                                    ref={editRef}
                                    testID={`DatePickerInput-${title}-Date`}
                                    style={[styles.editInput, !isEditing ? (displayText ? styles.valueText : styles.placeholderText) : null, mode === 'datetime' && styles.dateInput]}
                                    value={isEditing ? editText : (displayText?.split(' at ')[0] ?? '')}
                                    placeholder={mode === 'datetime' ? 'MM/DD/YYYY' : placeholderText}
                                    placeholderTextColor={colors.textSecondary}
                                    maxLength={isEditing ? 10 : undefined}
                                    readOnly={!isEditing || disabled}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    onChangeText={(raw) => {
                                        if (raw.length === 8 && !raw.includes('/') && /^\d{8}$/.test(raw)) {
                                            setEditText(`${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`);
                                        } else {
                                            setEditText(raw);
                                        }
                                    }}
                                    onKeyPress={handleEditKeyDown}
                                />
                                {mode === 'datetime' && (
                                    <>
                                        <Text style={styles.timeSeparator}>at</Text>
                                        <TextInput
                                            ref={editTimeRef}
                                            testID={`DatePickerInput-${title}-Time`}
                                            style={[styles.editInput, !isEditingTime ? (displayText ? styles.valueText : styles.placeholderText) : null, styles.timeInput]}
                                            value={isEditingTime ? editTimeText : (displayText?.split(' at ')[1] ?? '')}
                                            placeholder="HH:MM AM/PM"
                                            placeholderTextColor={colors.textSecondary}
                                            maxLength={isEditingTime ? 8 : undefined}
                                            readOnly={!isEditingTime}
                                            onFocus={handleFocusTime}
                                            onBlur={handleBlurTime}
                                            onChangeText={setEditTimeText}
                                            onKeyPress={handleEditTimeKeyDown}
                                        />
                                    </>
                                )}
                            </View>
                        ) : (
                            displayText
                                ? <Text style={styles.valueText}>{displayText}</Text>
                                : <Text style={styles.placeholderText}>{placeholderText}</Text>
                        )}
                    </View>
                </View>
            )}
            {value && !hideClearIcon && !disabled && (
                <Pressable hitSlop={20} onPress={() => { justClearedRef.current = true; setEditText(''); onUpdate(null); editRef.current?.focus(); }} style={styles.clearButton}>
                    <Unicon name="close" colorVariant="textSecondary" size={18}/>
                </Pressable>
            )}
        </View>
    );
}

const styles = StyleSheet.create(({colors, fonts, shadows, spacing, roundness, typography}, rt) => ({
    container: {
        width: "100%",
        marginVertical: spacing.sm,
    },
    input: (disabled, isNotOnSurface) => ({
        color: colors.text,
        borderRadius: roundness.sm,
        backgroundColor: isNotOnSurface ? colors.surface : colors.inputBackground,
        boxShadow: isNotOnSurface ? shadows.card : shadows.none,
        width: "100%",
        paddingHorizontal: 14,
        height: Math.max(44, Math.ceil(44 * rt.fontScale)),
        outlineStyle: 'none',
        justifyContent: "center",
        _web: {
            cursor: "pointer",
            transition: "all 0.15s ease",
            _focus: {
                boxShadow: disabled ? "" : shadows.input,
            },
            _hover: {
                backgroundColor: disabled ? (isNotOnSurface ? colors.surface : colors.inputBackground) : colors.hover,
            }
        }
    }),
    disabled: {
        opacity: 0.5,
        _web: {
            cursor: "not-allowed",
            _hover: {
                backgroundColor: colors.inputBackground,
            }
        }
    },
    inputRow: {
        flexDirection: "row", 
        alignItems: "center", 
        gap: 10,
    },
    valueText: {
        color: colors.text,
        fontSize: typography.body,
        fontFamily: fonts.body,
    },
    editInput: {
        color: colors.text,
        fontSize: typography.body,
        fontFamily: fonts.body,
        flex: 1,
        outlineStyle: 'none',
        padding: 0,
        includeFontPadding: false,
        textAlignVertical: 'center',
    },
    dateTimeInputs: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        gap: 4,
    },
    dateInput: {
        flex: 0,
    },
    timeSeparator: {
        color: colors.textSecondary,
        fontSize: typography.body,
        fontFamily: fonts.body,
        paddingHorizontal: 2,
    },
    timeInput: {
        flex: 0,
        minWidth: 90,
    },
    placeholderText: {
        color: colors.textSecondary,
        fontSize: typography.body,
        fontFamily: fonts.body,
    },
    labelContainer: {
        paddingBottom: 6,
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
    },
    labelText: {
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        fontSize: 13,
        flexShrink: 1,
    },
    required: {
        color: colors.error,
        fontSize: 14,
        fontFamily: fonts.body,
        marginLeft: -4,
    },
    clearButton: {
        position: "absolute",
        bottom: Math.max(12, Math.ceil((44 * rt.fontScale - 18) / 2)),
        right: 12,
        zIndex: 1,
    },
    pickerContainer: {
        minWidth: 320,
        width: '100%',
        maxWidth: 400,
    },
    timeOnlyContainer: {
        paddingVertical: 16,
        paddingHorizontal: 12,
        minWidth: 280,
        width: '100%',
        maxWidth: 400,
    },
    modalContent: {
        width: '100%',
        alignItems: "center",
        paddingBottom: spacing.lg,
    },
}));

export default DatePickerInput